import { dateLocale } from "@/lib/dateTime";
import { ListEmpty } from "@/components/patterns";
import { CallLink } from "@/components/calling/CallLink";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { CsvImportDialog, type ImportField } from "@/components/import/CsvImportDialog";
import { BulkEditDialog, type BulkField } from "@/components/inline/BulkEditDialog";
import { PreviewButton } from "@/components/preview/PreviewButton";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { InlineCell } from "@/components/inline/InlineCell";
import { ListPager } from "@/components/ui/list-pager";
import { SearchSelect } from "@/components/ui/search-select";
import { useToast } from "@/context/ToastContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Edit, Mail, Phone, Trash2, UserPlus } from "lucide-react";
import type { ContactRecord } from "@/types";
import { useAuth } from "@/context/AuthContext";
import { FilterBar, type ColumnOption } from "@/components/filters/FilterBar";
import { BulkBar, BulkButton, PageHeader, StatusPill, ViewsPanel, initialsOf } from "@/components/patterns";
import { ROLE_LABEL, ROLE_TONE } from "@/lib/buyingRoles";
import { WhatsAppButton } from "@/components/whatsapp/WhatsAppButton";
import { usePersistentFilters } from "@/lib/usePersistentFilters";
import { useFieldFilters, type FilterFieldDef } from "@/lib/useFieldFilters";
import { useSort } from "@/lib/useSort";
import { usePersistentColumns } from "@/lib/usePersistentColumns";
import { downloadCsv, fetchAllRows } from "@/lib/exportCsv";
import { dateRangeParams, ownerOptionsFromFacets, toQuery, type OwnerFacet } from "@/lib/filters";
import { cn, formatINRCompact } from "@/lib/utils";
import { CalendarDays as FilterDateIcon, UserCircle2 as FilterOwnerIcon } from "lucide-react";

// Remembered per browser; "all" / "any" mean no filter
const CONTACT_FILTER_DEFAULTS = { owner: "all", created: "any" };

/** A contact row with what the list adds: role on deals, open deals, last contact */
type ContactRow = ContactRecord & {
  buying_role?: string | null;
  open_deals?: number;
  open_value?: number;
  last_contacted_on?: string | null;
  last_contacted_what?: string | null;
};

type ContactStats = { counts: Record<string, number>; roles: Record<string, number>; accounts: number };

const CONTACT_COLUMNS: ColumnOption[] = [
  { key: "person", label: "Person", locked: true },
  { key: "account", label: "Account" },
  { key: "role", label: "Buying role" },
  { key: "reach", label: "Phone · email" },
  { key: "deals", label: "Open deals" },
  { key: "last", label: "Last contacted" },
  { key: "actions", label: "Reach out", locked: true },
];
const CONTACT_COLUMN_KEYS = CONTACT_COLUMNS.map((c) => c.key);
const QUIET_MS = 60 * 24 * 3600 * 1000;

function dayLabel(iso?: string | null) {
  if (!iso) return "Not yet";
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  return d.toLocaleDateString(dateLocale(), { day: "numeric", month: "short", ...(d.getFullYear() !== new Date().getFullYear() ? { year: "numeric" } : {}) });
}

const CONTACT_IMPORT_FIELDS: ImportField[] = [
  { key: "account_name", label: "Account", required: true, aliases: ["company", "company_name", "account_name", "customer"] },
  { key: "first_name", label: "First name", required: true, aliases: ["first", "firstname", "name"] },
  { key: "last_name", label: "Last name", aliases: ["last", "lastname", "surname"] },
  { key: "email", label: "Email", check: (v) => (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : "Email isn't valid") },
  { key: "phone", label: "Phone", aliases: ["telephone"] },
  { key: "mobile", label: "Mobile", aliases: ["cell"] },
  { key: "job_title", label: "Job title", aliases: ["title", "designation", "role"] },
];

const CONTACT_BULK_FIELDS: BulkField[] = [
  { field: "job_title", label: "Job title", kind: "text", nullable: true },
  { field: "phone", label: "Phone", kind: "phone", nullable: true },
  { field: "mobile", label: "Mobile", kind: "phone", nullable: true },
];

export function ContactsPage() {
  const [search, setSearch] = useState("");
  const [view, setView] = useState("all");
  const [role, setRole] = useState("all");
  const [stats, setStats] = useState<ContactStats | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const columns = usePersistentColumns("contacts", CONTACT_COLUMN_KEYS);
  const colHidden = (k: string) => (columns.has(k) ? "" : "hidden");
  const [accountLabel, setAccountLabel] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Dialogs
  const [addOpen, setAddOpen] = useState(false);
  const [editContact, setEditContact] = useState<ContactRecord | null>(null);
  const [deleteContact, setDeleteContact] = useState<ContactRecord | null>(null);

  // Form State
  const [form, setForm] = useState({
    account_id: "",
    first_name: "",
    last_name: "",
    job_title: "",
    email: "",
    phone: "",
    mobile: "",
    is_primary: false,
  });

  const { success, error } = useToast();
  const { user, hasPermission } = useAuth();
  const canEditInline = hasPermission("contact.update");
  const canUpdate = hasPermission("contact.update");
  const canDelete = hasPermission("contact.delete");
  const contactFilters = usePersistentFilters("contacts", CONTACT_FILTER_DEFAULTS);

  // "+ Add filter" on any field the API offers
  const fieldFilters = useFieldFilters("contacts");
  const sorting = useSort("contacts");
  const [fieldDefs, setFieldDefs] = useState<FilterFieldDef[] | null>(null);
  useEffect(() => {
    api.get("/api/Contact/FilterFields").then((res) => setFieldDefs(unwrapArray<FilterFieldDef>(res))).catch(() => setFieldDefs([]));
  }, []);
  const [contactOptions, setContactOptions] = useState<{ total: number; owners: OwnerFacet[] }>({ total: 0, owners: [] });

  const filterQuery = () => {
    const f = contactFilters.values;
    return toQuery({
      search: search.trim(),
      view: view === "all" ? "" : view,
      role: role === "all" ? "" : role,
      owner: f.owner,
      ...dateRangeParams(f.created, "created"),
      f: fieldFilters.param(fieldDefs), sort: sorting.sort,
    });
  };

  const loadStats = () => api.get("/api/Contact/Stats").then((r) => setStats(unwrapObject(r))).catch(() => undefined);
  useEffect(() => {
    loadStats();
  }, []);

  const list = usePagedList<ContactRow>({
    deps: [contactFilters.values, fieldFilters.values, fieldDefs, search, view, role, sorting.sort],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const query = filterQuery();
      try {
        const join = query ? "&" : "?";
        const [conRes, optRes] = await Promise.all([
          api.get(`/api/Contact/List${query}${join}page=${page}&page_size=30`),
          api.get("/api/Contact/FilterOptions").catch(() => null),
        ]);
        if (optRes) setContactOptions(unwrapObject(optRes));
        return unwrapPage<ContactRow>(conRes);
      } catch (err: any) {
        throw err;
      }
    },
  });
  const contacts = list.rows;
  const loading = list.loading;
  const loadData = () => {
    list.reload();
    loadStats();
  };
  useEffect(() => setSelected(new Set()), [view, role, search, contactFilters.values]);

  const topRoles = useMemo(
    () =>
      Object.entries(stats?.roles || {})
        .filter(([, n]) => n > 0)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5),
    [stats],
  );

  const blank = { account_id: "", first_name: "", last_name: "", job_title: "", email: "", phone: "", mobile: "", is_primary: false };
  const openAdd = () => {
    setForm(blank);
    setAccountLabel("");
    setAddOpen(true);
  };

  const openEdit = (c: ContactRecord) => {
    setAccountLabel(c.account?.name || "");
    setEditContact(c);
    setForm({
      account_id: c.account_id,
      first_name: c.first_name || "",
      last_name: c.last_name || "",
      job_title: c.job_title || "",
      email: c.email || "",
      phone: c.phone || "",
      mobile: c.mobile || "",
      is_primary: !!c.is_primary,
    });
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.account_id) {
      error("Validation", "Please select an account");
      return;
    }
    setActionLoading(true);
    try {
      await api.post("/api/Contact/Insert", form);
      success("Contact Added", "New contact registered");
      setAddOpen(false);
      loadData();
    } catch (err: any) {
      error("Creation Failed", err.message || "Could not add contact");
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editContact) return;
    setActionLoading(true);
    try {
      await api.put(`/api/Contact/Update/${editContact.id}`, form);
      success("Contact Updated", "Contact details saved");
      setEditContact(null);
      loadData();
    } catch (err: any) {
      error("Update Failed", err.message || "Could not update contact");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteSubmit = async () => {
    if (!deleteContact) return;
    setActionLoading(true);
    try {
      await api.delete(`/api/Contact/Delete/${deleteContact.id}`);
      success("Contact Deleted", "Contact removed");
      setDeleteContact(null);
      loadData();
    } catch (err: any) {
      error("Delete Failed", err.message || "Could not delete contact");
    } finally {
      setActionLoading(false);
    }
  };

  // Selection and CSV
  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const allOnPage = contacts.length > 0 && contacts.every((c) => selected.has(c.id));
  const picked = contacts.filter((c) => selected.has(c.id));
  const EXPORT_COLUMNS: [string, (c: ContactRow) => string | number | null | undefined][] = [
    ["Name", (c) => `${c.first_name} ${c.last_name || ""}`.trim()],
    ["Job title", (c) => c.job_title || ""],
    ["Account", (c) => c.account?.name || ""],
    ["Buying role", (c) => (c.buying_role ? ROLE_LABEL[c.buying_role] || c.buying_role : "")],
    ["Email", (c) => c.email || ""],
    ["Mobile", (c) => c.mobile || ""],
    ["Phone", (c) => c.phone || ""],
    ["Primary", (c) => (c.is_primary ? "Yes" : "")],
    ["Open deals", (c) => c.open_deals ?? 0],
    ["Last contacted", (c) => (c.last_contacted_on ? new Date(c.last_contacted_on).toLocaleDateString(dateLocale()) : "")],
  ];
  const fileName = () => `contacts-${new Date().toISOString().slice(0, 10)}.csv`;
  const exportAll = async () => {
    setExporting(true);
    try {
      const { rows, total } = await fetchAllRows<ContactRow>("/api/Contact/List", filterQuery());
      downloadCsv(fileName(), EXPORT_COLUMNS, rows);
      if (total > rows.length) error("Export is partial", `The first ${rows.length} of ${total} contacts were saved. Narrow the filters to get the rest.`);
    } catch (e: any) {
      error("Could not export", e?.message || "Try again");
    } finally {
      setExporting(false);
    }
  };

  const c = stats?.counts || {};
  const name = (con: ContactRecord) => `${con.first_name} ${con.last_name || ""}`.trim();

  return (
    <div className="space-y-4">
      <ViewsPanel
        title="Contacts"
        subtitle="People you sell to"
        saved={{
          module: "contacts",
          getConfig: () => ({ view, role, search, filters: contactFilters.values, fieldFilters: fieldFilters.values, sort: sorting.sort }),
          apply: (cfg) => {
            setView(cfg.view || "all");
            setRole(cfg.role || "all");
            setSearch(cfg.search || "");
            contactFilters.replace(cfg.filters || {});
            fieldFilters.setAll(cfg.fieldFilters);
            sorting.setSort(cfg.sort);
          },
        }}
        active={view}
        onSelect={setView}
        groups={[
          {
            items: [
              { key: "all", label: "All contacts", count: c.all },
              { key: "mine", label: "My contacts", count: c.mine },
              { key: "primary", label: "Primary contacts", count: c.primary },
              { key: "decision_makers", label: "Decision makers", count: c.decision_makers },
              { key: "on_open_deals", label: "On open deals", count: c.on_open_deals },
              { key: "quiet", label: "Not contacted 60+ days", count: c.quiet, tone: "warning" },
              { key: "duplicates", label: "Possible duplicates", count: c.duplicates, tone: "danger" },
            ],
          },
        ]}
        footer={
          topRoles.length > 0 ? (
            <div className="mt-3 flex flex-col gap-1.5 px-2.5">
              <div className="pb-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">Buying role</div>
              {topRoles.map(([key, n]) => (
                <button key={key} type="button" onClick={() => setRole(role === key ? "all" : key)} className="flex flex-col gap-1 text-left">
                  <div className="flex justify-between text-xs">
                    <span className={cn("truncate", role === key ? "font-semibold text-primary" : "text-foreground")}>{ROLE_LABEL[key]}</span>
                    <span className="font-mono text-muted-foreground">{n}</span>
                  </div>
                  <div className="h-1 rounded-sm bg-border">
                    <div className="h-full rounded-sm bg-primary" style={{ width: `${Math.round((n / (topRoles[0]?.[1] || 1)) * 100)}%` }} />
                  </div>
                </button>
              ))}
            </div>
          ) : undefined
        }
      />

      <PageHeader
        title="Contacts"
        summary={list.total}
        actions={
          <>
            {hasPermission("contact.create") && (
              <Button variant="outline" size="sm" className="hidden md:inline-flex" onClick={() => setImportOpen(true)}>
                Import
              </Button>
            )}
            <Button variant="outline" size="sm" className="hidden md:inline-flex" onClick={exportAll} disabled={exporting}>
              Export
            </Button>
            {hasPermission("contact.create") && (
              <Button size="sm" data-new-record onClick={openAdd}>
                <UserPlus className="size-4 mr-1.5" />
                New contact
              </Button>
            )}
          </>
        }
      />

      {stats && (
        <p className="text-[13px] text-muted-foreground">
          {c.all ?? 0} people at {stats.accounts} account{stats.accounts === 1 ? "" : "s"}
          {c.duplicates ? <> · <span className="text-danger">{c.duplicates} possible duplicate{c.duplicates === 1 ? "" : "s"}</span></> : null}
        </p>
      )}

      <CsvImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Import contacts"
        noun="contacts"
        fields={CONTACT_IMPORT_FIELDS}
        create={async (r) => {
          const accountName = r.account_name || "";
          // The account is found by its name: it has to exist already
          const found = unwrapPage<{ id: string; name: string }>(await api.get(`/api/Account/List?search=${encodeURIComponent(accountName)}&page_size=20`)).rows.filter((a) => a.name.trim().toLowerCase() === accountName.trim().toLowerCase());
          if (found.length === 0) throw new Error(`No account named "${accountName}": add or import it first`);
          if (found.length > 1) throw new Error(`More than one account is named "${accountName}"`);
          await api.post("/api/Contact/Insert", { account_id: found[0]!.id, first_name: r.first_name, last_name: r.last_name || undefined, email: r.email || undefined, phone: r.phone || undefined, mobile: r.mobile || undefined, job_title: r.job_title || undefined });
        }}
        onDone={() => list.reload()}
        hint="One contact per row. The account must already exist: it is found by its name."
      />

      <BulkEditDialog open={bulkEditOpen} onClose={() => setBulkEditOpen(false)} list="Contact" ids={Array.from(selected)} fields={CONTACT_BULK_FIELDS} onDone={() => { setSelected(new Set()); list.reload(); }} />

      <BulkBar count={selected.size} onClear={() => setSelected(new Set())}>
        {canEditInline && <BulkButton onClick={() => setBulkEditOpen(true)}>Edit a field</BulkButton>}
        <BulkButton onClick={() => downloadCsv(fileName(), EXPORT_COLUMNS, picked)}>Export</BulkButton>
      </BulkBar>

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: "Search name, account, phone, email" }}
        fields={[
          { name: "owner", label: "Owner", icon: FilterOwnerIcon, type: "select", options: ownerOptionsFromFacets(contactOptions.owners, contactOptions.total, user?.Id), defaultValue: "all" },
          { name: "created", label: "Created", icon: FilterDateIcon, type: "date", dates: "past", defaultValue: "any" },
        ]}
        values={contactFilters.values}
        onChange={(n, value) => contactFilters.set(n as keyof typeof CONTACT_FILTER_DEFAULTS, value)}
        onReset={() => {
          contactFilters.reset();
          setRole("all");
        }}
        quickKey="contacts"
        fieldFilters={{ fields: fieldDefs || [], value: fieldFilters.values, onChange: fieldFilters.setAll, suggestUrl: "/api/Contact/FilterValues" }}
        sort={{ fields: fieldDefs || [], value: sorting.sort, onChange: sorting.setSort }}
        shown={list.total}
        total={list.total}
        noun="contacts"
        columns={{ options: CONTACT_COLUMNS, value: columns.visible, onChange: columns.setVisible }}
      >
        {role !== "all" && (
          <button type="button" onClick={() => setRole("all")} className="inline-flex h-7 items-center gap-1.5 rounded-md bg-accent px-2.5 text-xs font-medium text-accent-foreground">
            Role: {ROLE_LABEL[role]} <span aria-hidden="true">×</span>
            <span className="sr-only">Remove role filter</span>
          </button>
        )}
      </FilterBar>

      {loading && contacts.length === 0 ? (
        <TableSkeleton />
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <Table className="min-w-[960px]">
            <TableHeader>
              <TableRow className="bg-panel hover:bg-panel border-b border-border">
                <TableHead className="w-10">
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    checked={allOnPage}
                    onChange={() => setSelected(allOnPage ? new Set() : new Set(contacts.map((x) => x.id)))}
                    className="rounded border-border size-4 accent-primary cursor-pointer"
                  />
                </TableHead>
                {([
                  ["person", "Person", ""],
                  ["account", "Account", ""],
                  ["role", "Buying role", ""],
                  ["reach", "Phone · email", ""],
                  ["deals", "Open deals", ""],
                  ["last", "Last contacted", ""],
                  ["actions", "Reach out", "text-right"],
                ] as [string, string, string][]).map(([k, label, align]) => (
                  <TableHead key={k} className={cn("text-[11px] font-semibold uppercase tracking-wide text-muted-foreground", align, colHidden(k))}>
                    {label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="p-0">
                    <ListEmpty
                      noun="contacts" error={list.error} onRetry={list.reload}
                      icon={UserPlus}
                      filtered={!!search.trim() || view !== "all" || role !== "all" || contactFilters.activeCount > 0 || fieldFilters.activeCount > 0}
                      onClear={() => {
                        setSearch("");
                        setView("all");
                        setRole("all");
                        contactFilters.reset();
                        fieldFilters.reset();
                      }}
                      createLabel="+ New contact"
                      onCreate={hasPermission("contact.create") ? openAdd : undefined}
                      firstUse="Contacts are the people at your accounts. Add the first one."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((con) => {
                  const quiet = con.last_contacted_on ? Date.now() - new Date(con.last_contacted_on).getTime() > QUIET_MS : false;
                  const phone = con.mobile || con.phone;
                  return (
                    <TableRow key={con.id} className={cn("group/row text-xs", selected.has(con.id) && "bg-primary/5")}>
                      <TableCell>
                        <input type="checkbox" aria-label={`Select ${name(con)}`} checked={selected.has(con.id)} onChange={() => toggle(con.id)} className="rounded border-border size-4 accent-primary cursor-pointer" />
                      </TableCell>
                      <TableCell className={colHidden("person")}>
                        <div className="flex items-center gap-2.5">
                          <div className="size-7 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-bold text-[10px] shrink-0">{initialsOf(name(con))}</div>
                          <div className="min-w-0">
                            <Link to={`/contacts/${con.id}`} className="font-semibold text-foreground hover:text-primary hover:underline block truncate">
                              {name(con)}
                            </Link>
                            <PreviewButton entity="Contact" id={con.id} label={name(con)} className="ml-1 align-middle" />
                            <InlineCell list="Contact" id={con.id} field="job_title" kind="text" value={con.job_title} display={<span className="text-[11.5px] text-muted-foreground">{con.job_title || "—"}</span>} enabled={canEditInline} onSaved={(row) => list.patchRow(con.id, row)} onConflict={list.reload} />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className={colHidden("account")}>
                        {con.account ? (
                          <Link to={`/accounts/${con.account_id || con.account.id}`} className="font-medium text-primary hover:underline">
                            {con.account.name}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className={colHidden("role")}>
                        {con.buying_role ? <StatusPill tone={ROLE_TONE[con.buying_role] || "neutral"} dot={false}>{ROLE_LABEL[con.buying_role] || con.buying_role}</StatusPill> : con.is_primary ? <StatusPill tone="primary" dot={false}>Primary</StatusPill> : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className={colHidden("reach")}>
                        <div className="font-mono text-xs">{phone || "—"}</div>
                        <div className="text-[11.5px] text-muted-foreground max-w-[200px] truncate">{con.email || ""}</div>
                      </TableCell>
                      <TableCell className={cn("text-muted-foreground", colHidden("deals"))}>
                        {con.open_deals ? (
                          <>
                            <span className="text-foreground">{con.open_deals}</span> · <span className="font-mono">{formatINRCompact(con.open_value || 0)}</span>
                          </>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className={colHidden("last")}>
                        <div className={cn(quiet ? "font-semibold text-warning" : "text-foreground")}>{dayLabel(con.last_contacted_on)}</div>
                        <div className="text-[11.5px] text-muted-foreground max-w-[180px] truncate">{con.last_contacted_what || ""}</div>
                      </TableCell>
                      <TableCell className={cn("text-right whitespace-nowrap", colHidden("actions"))}>
                        <div className="inline-flex items-center gap-1">
                          {phone && (
                            <Button asChild variant="outline" size="icon-sm" title="Call" aria-label={`Call ${name(con)}`}>
                              <CallLink type="CONTACT" id={con.id} number={phone}>
                                <Phone className="size-3.5" />
                              </CallLink>
                            </Button>
                          )}
                          {phone && <WhatsAppButton phone={phone} name={name(con)} contactId={con.id} />}
                          {con.email && (
                            <Button asChild variant="outline" size="icon-sm" title="Email" aria-label={`Email ${name(con)}`}>
                              <a href={`mailto:${con.email}`}>
                                <Mail className="size-3.5" />
                              </a>
                            </Button>
                          )}
                          {canUpdate && (
                            <Button variant="ghost" size="icon" title="Edit" onClick={() => openEdit(con)} className="size-8">
                              <Edit className="size-3.5" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button variant="ghost" size="icon" title="Delete" onClick={() => setDeleteContact(con)} className="size-8 text-destructive">
                              <Trash2 className="size-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={loading} />
        </div>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={addOpen || !!editContact} onOpenChange={(open) => !open && (setAddOpen(false), setEditContact(null))}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <form onSubmit={editContact ? handleEditSubmit : handleAddSubmit}>
            <DialogHeader>
              <DialogTitle>{editContact ? "Edit Contact" : "Add Contact"}</DialogTitle>
              <DialogDescription>
                Assign this contact to an account organization and configure details.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3.5 py-4 text-xs sm:text-sm">
              <div className="space-y-1.5">
                <Label htmlFor="contact_account">Organization Account *</Label>
                <SearchSelect
                  value={form.account_id}
                  selectedLabel={accountLabel}
                  endpoint="/api/Account/List"
                  placeholder="Search accounts"
                  getLabel={(a) => a.name}
                  onChange={(id, row) => {
                    setAccountLabel(row?.name || "");
                    setForm((p) => ({ ...p, account_id: id }));
                  }}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="con_first_name">First Name *</Label>
                  <Input
                    id="con_first_name"
                    value={form.first_name}
                    onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="con_last_name">Last Name</Label>
                  <Input
                    id="con_last_name"
                    value={form.last_name}
                    onChange={(e) => setForm((p) => ({ ...p, last_name: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="con_job">Job Title</Label>
                <Input
                  id="con_job"
                  placeholder="e.g. Procurement Lead, Director"
                  value={form.job_title}
                  onChange={(e) => setForm((p) => ({ ...p, job_title: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="con_email">Work Email</Label>
                  <Input
                    id="con_email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="con_phone">Direct Phone</Label>
                  <Input
                    id="con_phone"
                    value={form.phone}
                    onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="is_primary_checkbox"
                  checked={form.is_primary}
                  onChange={(e) => setForm((p) => ({ ...p, is_primary: e.target.checked }))}
                  className="rounded border-input text-primary focus:ring-primary size-4"
                />
                <Label htmlFor="is_primary_checkbox" className="cursor-pointer font-medium">
                  Designate as Primary Account Contact
                </Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => (setAddOpen(false), setEditContact(null))}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? "Saving..." : editContact ? "Save Changes" : "Create Contact"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteContact} onOpenChange={(open) => !open && setDeleteContact(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delete Contact</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteContact?.first_name} {deleteContact?.last_name}</strong>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteContact(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteSubmit} disabled={actionLoading}>
              {actionLoading ? "Deleting..." : "Delete Contact"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
